/**
 * Losi Code Annotation (chat canvas)
 *
 * Renders the live coding-run activity for a losiCode tool call inside chat.
 * Once spaceId + taskId are known, this card drives its own live feed —
 * independent of whether the LLM is mid-turn, idle, or never calls
 * get_coding_run_status again.
 *
 * Live updates (all run together while mounted + run not terminal):
 * 1. SSE /api/spaces/:id/tasks/:taskId/coding-activity/stream
 * 2. Supabase realtime on space_task_losi_run_events
 * 3. Poll coding-context every 2s (always, as a reliability backstop)
 *
 * Primary responsibilities:
 * - Extract the losiCode tool result from the message annotation
 * - Keep status/activity/PR live without requiring further LLM tool calls
 * - Render run status, repo, live activity timeline, and PR/summary
 */

'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Github,
  GitBranch,
  GitFork,
  Terminal,
  FilePlus,
  FileText,
  FileEdit,
  Search,
  Globe,
  ListChecks,
  Workflow,
  Wrench,
  MessageSquare,
  Play,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ExternalLink,
  type LucideIcon
} from 'lucide-react'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { Annotation, Annotation2 } from '@/types/annotation'
import {
  buildCodingActivityLines,
  type CodingActivityIcon,
  type CodingRunTimelineEntry
} from '@/lib/spaces/coding-run-humanizer'

// --------------

type LosiCodeRunStatus =
  | 'queued'
  | 'running'
  | 'waiting'
  | 'completed'
  | 'error'
  | 'cancelled'

type LosiCodeActivityLine = {
  id: string
  level: string
  text: string
  icon: CodingActivityIcon
}

type LosiCodeToolResult = {
  ok?: boolean
  error?: string
  runId?: string
  status?: LosiCodeRunStatus
  spaceId?: string
  taskId?: string
  latestSummary?: string | null
  prUrl?: string | null
  repo?: {
    repoFullName: string
    branch: string
    allowAgentCreatePr: boolean
  } | null
  activity?: LosiCodeActivityLine[]
  needsSpaceTask?: boolean
}

interface LosiCodeAnnotationProps {
  annotation: Annotation | Annotation2
  toolKey?: string
}

// --------------

const ACTIVITY_ICON: Record<CodingActivityIcon, LucideIcon> = {
  edit: FileEdit,
  write: FilePlus,
  read: FileText,
  command: Terminal,
  search: Search,
  web: Globe,
  plan: ListChecks,
  task: Workflow,
  tool: Wrench,
  text: MessageSquare,
  repo: GitFork,
  branch: GitBranch,
  start: Play,
  check: CheckCircle2,
  error: AlertTriangle
}

const STATUS_LABEL: Record<LosiCodeRunStatus, string> = {
  queued: 'Queued',
  running: 'Working',
  waiting: 'Waiting',
  completed: 'Done',
  error: 'Failed',
  cancelled: 'Cancelled'
}

const ACTIVE_STATUSES = new Set<LosiCodeRunStatus>([
  'queued',
  'running',
  'waiting'
])

const TERMINAL_STATUSES = new Set<LosiCodeRunStatus>([
  'completed',
  'error',
  'cancelled'
])

const POLL_MS = 2000

// --------------

/** Pull the losiCode tool result out of the annotation record. */
function readLosiCodeResult(
  annotation: Annotation | Annotation2,
  toolKey?: string
): LosiCodeToolResult | null {
  const record = annotation as Record<string, unknown>

  const entry =
    (toolKey ? record[toolKey] : undefined) ??
    record['losiCode__start_coding_run'] ??
    record['losiCode__run_verification'] ??
    record['losiCode__get_coding_run_status'] ??
    record['losiCode__ensure_coding_task'] ??
    Object.entries(record).find(([key]) => key.startsWith('losiCode__'))?.[1]

  if (!entry || typeof entry !== 'object') return null

  const wrapped = entry as { result?: unknown }
  const result =
    wrapped.result && typeof wrapped.result === 'object' ? wrapped.result : entry

  return result as LosiCodeToolResult
}

// --------------

type CodingContextPayload = {
  latestRun?: {
    id?: string
    status?: LosiCodeRunStatus
    latest_summary?: string | null
    pr_url?: string | null
    preview_url?: string | null
    error?: string | null
  } | null
  timeline?: CodingRunTimelineEntry[]
  effectiveRepoLink?: {
    repo_full_name?: string
    branch?: string
    allow_agent_create_pr?: boolean
  } | null
}

async function fetchCodingContext(
  spaceId: string,
  taskId: string
): Promise<CodingContextPayload | null> {
  try {
    const res = await fetch(
      `/api/spaces/${encodeURIComponent(spaceId)}/tasks/${encodeURIComponent(taskId)}/coding-context`,
      { cache: 'no-store', credentials: 'same-origin' }
    )
    if (!res.ok) return null
    return (await res.json()) as CodingContextPayload
  } catch {
    return null
  }
}

// --------------

function StatusBadge({ status }: { status?: LosiCodeRunStatus }) {
  if (!status) return null
  const active = ACTIVE_STATUSES.has(status)

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
        status === 'error'
          ? 'border-destructive/20 bg-destructive/10 text-destructive'
          : status === 'completed'
            ? 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
            : status === 'cancelled'
              ? 'border-border bg-muted text-muted-foreground'
              : 'border-primary/25 bg-primary/10 text-primary'
      )}
    >
      {active ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
      {STATUS_LABEL[status]}
    </span>
  )
}

// --------------

export const LosiCodeAnnotation: React.FC<LosiCodeAnnotationProps> = ({
  annotation,
  toolKey
}) => {
  const initial = readLosiCodeResult(annotation, toolKey)
  const [live, setLive] = useState<LosiCodeToolResult | null>(initial)
  const listRef = useRef<HTMLOListElement | null>(null)
  const statusRef = useRef<LosiCodeRunStatus | undefined>(
    live?.status ?? initial?.status
  )

  const spaceId = live?.spaceId ?? initial?.spaceId
  const taskId = live?.taskId ?? initial?.taskId
  const runId = live?.runId ?? initial?.runId
  const status = live?.status ?? initial?.status
  statusRef.current = status

  // Keep streaming until we have confirmed a terminal status from the server
  // (not just a stale annotation snapshot). Unknown/missing status still streams.
  const shouldStream = Boolean(
    spaceId &&
      taskId &&
      (!status || ACTIVE_STATUSES.has(status) || !TERMINAL_STATUSES.has(status))
  )

  const applyContext = useCallback((payload: CodingContextPayload) => {
    const timeline = payload.timeline ?? []
    const activity = buildCodingActivityLines(timeline)
    const link = payload.effectiveRepoLink
    setLive((prev) => {
      const nextStatus = payload.latestRun?.status ?? prev?.status
      const nextSummary =
        payload.latestRun?.status === 'error' && payload.latestRun.error
          ? payload.latestRun.error
          : payload.latestRun?.latest_summary ?? prev?.latestSummary ?? null
      return {
        ...(prev ?? {}),
        ok: true,
        runId: payload.latestRun?.id ?? prev?.runId,
        status: nextStatus,
        prUrl: payload.latestRun?.pr_url ?? prev?.prUrl ?? null,
        latestSummary: nextSummary,
        activity,
        repo: link?.repo_full_name
          ? {
              repoFullName: link.repo_full_name,
              branch: link.branch || prev?.repo?.branch || 'main',
              allowAgentCreatePr: Boolean(link.allow_agent_create_pr)
            }
          : prev?.repo ?? null
      }
    })
  }, [])

  const refresh = useCallback(async () => {
    if (!spaceId || !taskId) return
    // Still refresh when the tab is hidden so the card is current on return;
    // skip only when the document API is unavailable.
    const payload = await fetchCodingContext(spaceId, taskId)
    if (payload) applyContext(payload)
  }, [spaceId, taskId, applyContext])

  // Sync when the parent annotation object gets a newer tool result (e.g. status).
  useEffect(() => {
    const next = readLosiCodeResult(annotation, toolKey)
    if (!next) return
    setLive((prev) => {
      // Prefer live activity/status already streamed; merge ids/repo from annotation.
      if (!prev) return next
      return {
        ...next,
        ...prev,
        spaceId: prev.spaceId || next.spaceId,
        taskId: prev.taskId || next.taskId,
        runId: prev.runId || next.runId,
        repo: prev.repo || next.repo,
        // Keep the freshest non-empty activity; prefer live status already
        // streamed from the coding runner over a stale annotation snapshot.
        activity:
          (prev.activity?.length ?? 0) >= (next.activity?.length ?? 0)
            ? prev.activity
            : next.activity,
        status: prev.status ?? next.status,
        prUrl: prev.prUrl || next.prUrl,
        latestSummary: prev.latestSummary || next.latestSummary
      }
    })
  }, [annotation, toolKey])

  // Autonomous live feed — does NOT depend on the LLM turn.
  // Poll + SSE + realtime all stay up while this card is mounted so
  // coding-runner events keep landing even when chat is idle.
  useEffect(() => {
    if (!spaceId || !taskId) return

    let cancelled = false
    let es: EventSource | null = null
    let pollId: number | null = null
    let sseRetryId: number | null = null
    let supabase: ReturnType<typeof createClientComponentClient> | null = null
    let channel: ReturnType<
      ReturnType<typeof createClientComponentClient>['channel']
    > | null = null

    const applySse = (ev: MessageEvent) => {
      try {
        const payload = JSON.parse(String(ev.data)) as {
          status?: LosiCodeRunStatus | null
          latestSummary?: string | null
          activity?: LosiCodeActivityLine[]
          runId?: string
        }
        if (cancelled) return
        if (payload.status == null && !payload.activity?.length && !payload.runId) {
          // idle heartbeat — keep listening
          return
        }
        setLive((prev) => ({
          ...(prev ?? {}),
          ok: true,
          runId: payload.runId ?? prev?.runId,
          status: payload.status ?? prev?.status,
          latestSummary: payload.latestSummary ?? prev?.latestSummary ?? null,
          activity: payload.activity ?? prev?.activity ?? []
        }))
      } catch {
        // ignore malformed frames
      }
    }

    const startSse = () => {
      if (cancelled || es) return

      const url = `/api/spaces/${encodeURIComponent(spaceId)}/tasks/${encodeURIComponent(taskId)}/coding-activity/stream`
      es = new EventSource(url)
      es.addEventListener('activity', applySse as EventListener)
      es.addEventListener('idle', applySse as EventListener)
      es.addEventListener('done', (ev) => {
        applySse(ev as MessageEvent)
        es?.close()
        es = null
        // Re-open shortly — a new coding run can start without another LLM call.
        if (!cancelled) {
          sseRetryId = window.setTimeout(() => {
            sseRetryId = null
            if (!cancelled && !es) startSse()
          }, 1500)
        }
      })
      es.onerror = () => {
        // Drop dead SSE; polling + realtime keep the card alive.
        es?.close()
        es = null
        if (!cancelled && sseRetryId == null) {
          sseRetryId = window.setTimeout(() => {
            sseRetryId = null
            if (!cancelled && !es) startSse()
          }, 2500)
        }
      }
    }

    // Immediate hydrate + continuous poll (works even if LLM is idle/busy).
    void refresh()
    pollId = window.setInterval(() => {
      if (cancelled) return
      void refresh()
    }, POLL_MS)

    startSse()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refresh()
        if (!es) startSse()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    supabase = createClientComponentClient()
    // Prefer task-scoped events so a follow-up run on the same task is visible
    // without remounting the annotation.
    channel = supabase
      .channel(`losi-code-activity:${taskId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'space_task_losi_run_events',
          filter: `task_id=eq.${taskId}`
        },
        () => {
          void refresh()
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'space_task_losi_runs',
          filter: `task_id=eq.${taskId}`
        },
        () => {
          void refresh()
          if (!es) startSse()
        }
      )
      .subscribe()

    return () => {
      cancelled = true
      es?.close()
      if (pollId != null) window.clearInterval(pollId)
      if (sseRetryId != null) window.clearTimeout(sseRetryId)
      document.removeEventListener('visibilitychange', onVisibility)
      if (supabase && channel) void supabase.removeChannel(channel)
    }
  }, [spaceId, taskId, refresh])

  // Auto-scroll activity to the newest line when it grows.
  const activity = live?.activity ?? initial?.activity ?? []
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [activity.length, activity[activity.length - 1]?.id])

  const result = live ?? initial
  if (!result) return null

  // Still show the card (and keep streaming) for errors that include task ids.
  if (result.ok === false && result.error && !spaceId && !taskId) {
    return (
      <div className="border-destructive/20 bg-destructive/10 text-destructive rounded-2xl border p-3 text-xs leading-5">
        <span className="font-medium">Losi Code: </span>
        {result.error}
        {result.needsSpaceTask ? (
          <span className="text-destructive/80 mt-1 block">
            Connect a GitHub repo to a Space (connector card), then retry —
            Losi Code will start from chat without a manual task setup.
          </span>
        ) : null}
      </div>
    )
  }

  const liveStatus = result.status
  const isLive = Boolean(liveStatus && ACTIVE_STATUSES.has(liveStatus))

  return (
    <div className="border-border bg-background text-foreground overflow-hidden rounded-2xl border">
      <div className="border-border/80 flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <div className="bg-primary/10 text-primary flex size-8 items-center justify-center rounded-full">
          <Terminal className="size-4" aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-[11px] font-medium uppercase tracking-[0.14em]">
            Losi Code
            {isLive || shouldStream ? (
              <span className="text-primary ml-2 inline-flex items-center gap-1 normal-case tracking-normal">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-primary" />
                </span>
                live
              </span>
            ) : null}
          </div>
          {result.repo ? (
            <a
              href={`https://github.com/${result.repo.repoFullName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground inline-flex items-center gap-1 truncate text-sm font-semibold hover:underline"
            >
              <Github className="size-3.5 opacity-70" aria-hidden />
              {result.repo.repoFullName}
              <span className="text-muted-foreground inline-flex items-center gap-1 font-normal">
                <GitBranch className="size-3 opacity-70" aria-hidden />
                {result.repo.branch}
              </span>
            </a>
          ) : (
            <div className="text-foreground text-sm font-semibold">Coding run</div>
          )}
        </div>
        <div className="ml-auto">
          <StatusBadge status={result.status} />
        </div>
      </div>

      <div className="space-y-3 p-4">
        {result.ok === false && result.error ? (
          <p className="text-destructive text-xs leading-5">{result.error}</p>
        ) : null}

        {result.latestSummary ? (
          <p className="text-muted-foreground text-xs leading-5">
            {result.latestSummary}
          </p>
        ) : null}

        {activity.length > 0 ? (
          <div className="border-border/50 bg-muted/20 rounded-lg border">
            <div className="text-muted-foreground border-border/50 flex items-center gap-1.5 border-b px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide">
              <Terminal className="size-3.5" aria-hidden />
              Live activity
              {isLive ? (
                <Loader2 className="ml-auto size-3 animate-spin" aria-hidden />
              ) : null}
            </div>
            <ol
              ref={listRef}
              className="max-h-72 overflow-y-auto px-3 py-2 font-mono text-xs"
            >
              {activity.map((entry) => {
                const Icon = ACTIVITY_ICON[entry.icon] ?? Wrench
                const isError = entry.level === 'error' || entry.icon === 'error'
                return (
                  <li
                    key={entry.id}
                    className="flex items-start gap-2 py-0.5 leading-relaxed"
                  >
                    <Icon
                      className={cn(
                        'mt-0.5 size-3.5 shrink-0',
                        isError ? 'text-destructive' : 'text-muted-foreground'
                      )}
                      aria-hidden
                    />
                    <span
                      className={cn(
                        'whitespace-pre-wrap break-words',
                        isError ? 'text-destructive' : 'text-foreground/80'
                      )}
                    >
                      {entry.text}
                    </span>
                  </li>
                )
              })}
            </ol>
          </div>
        ) : (
          <div className="border-border bg-muted/20 text-muted-foreground rounded-lg border border-dashed p-3 text-xs">
            {isLive || shouldStream
              ? 'Waiting for the coding agent — activity will appear here live.'
              : 'No activity yet.'}
          </div>
        )}

        {result.prUrl ? (
          <Button asChild size="sm" variant="secondary" className="h-8 gap-1.5">
            <a href={result.prUrl} target="_blank" rel="noopener noreferrer">
              <Github className="size-4" aria-hidden />
              View pull request
              <ExternalLink className="size-3.5 opacity-70" aria-hidden />
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export default LosiCodeAnnotation
